import { useEffect } from "react";
import { trackConversion, trackCtaClick, trackPageView } from "../../utils/tag-manager";

export const TagManagerPageTracker = () => {
    useEffect(() => {
        const getLinkType = (href: string): string => {
            if (href.startsWith("mailto:")) {
                return "mailto";
            }

            if (href.startsWith("tel:")) {
                return "phone";
            }

            if (href.startsWith("#")) {
                return "anchor";
            }

            try {
                const resolved = new URL(href, window.location.origin);
                if (resolved.origin !== window.location.origin) {
                    return "external";
                }
                return "internal";
            } catch {
                return "internal";
            }
        };

        const sendPageView = () => {
            const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            trackPageView({
                path: currentPath,
                title: document.title,
                language: document.documentElement.lang || "de",
            });
        };

        const originalPushState = window.history.pushState.bind(window.history);
        const originalReplaceState = window.history.replaceState.bind(window.history);

        const handleLocationChange = () => {
            sendPageView();
        };

        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }

            const anchor = target.closest("a[href]");
            if (!anchor || !(anchor instanceof HTMLAnchorElement)) {
                return;
            }

            const href = anchor.getAttribute("href");
            if (!href) {
                return;
            }

            const normalizedHref = href.trim();
            const isLiveDemoConversion = !!target.closest(".gtag-livedemo");
            if (
                !normalizedHref ||
                normalizedHref === "#" ||
                normalizedHref.startsWith("javascript:") ||
                anchor.dataset.gtmTrack === "off" ||
                anchor.dataset.gtmTracked === "cta-hook"
            ) {
                return;
            }

            if (isLiveDemoConversion) {
                trackConversion({
                    conversionName: "live_demo_click",
                    conversionCategory: "secondary",
                    pagePath: `${window.location.pathname}${window.location.search}`,
                    href: normalizedHref,
                });
            }

            trackCtaClick({
                label: anchor.textContent?.trim(),
                href: normalizedHref,
                urlType: getLinkType(normalizedHref),
                pagePath: `${window.location.pathname}${window.location.search}`,
            });
        };

        window.history.pushState = (...args: Parameters<typeof window.history.pushState>) => {
            originalPushState(...args);
            window.dispatchEvent(new Event("locationchange"));
        };

        window.history.replaceState = (...args: Parameters<typeof window.history.replaceState>) => {
            originalReplaceState(...args);
            window.dispatchEvent(new Event("locationchange"));
        };

        window.addEventListener("popstate", handleLocationChange);
        window.addEventListener("hashchange", handleLocationChange);
        window.addEventListener("locationchange", handleLocationChange);
        document.addEventListener("click", handleDocumentClick);

        sendPageView();

        return () => {
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
            window.removeEventListener("popstate", handleLocationChange);
            window.removeEventListener("hashchange", handleLocationChange);
            window.removeEventListener("locationchange", handleLocationChange);
            document.removeEventListener("click", handleDocumentClick);
        };
    }, []);

    return null;
};
